import { TFunction } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Section from '../../componentsv2/Section';
import Typography from '../../componentsv2/Typography';
import ProgressBar from '../../componentsv2/ProgressBar';
import Banner, { BannerType } from '../../componentsv2/Banner';
import { useUserLocation } from '../../hooks/useUserLocation';
import { COLORS } from '../../themev2';
import { formatCreditLineCurrency } from '../../utils/helpers';
import { formatNumericValue } from '../../utils/common';
import { getFactoringCap, getFactoringRemainingPercent } from '../../utils/factoringCollateral';
import { RevolvingCreditLineClientDetail } from '../../types/servicing';
import { CreditLineProductType } from '../../types/factoringCreditLine';

interface CreditLineCardProps {
  creditLine?: RevolvingCreditLineClientDetail;
  compactMode: boolean;
  t: TFunction;
}

export default function CreditLineCard({ creditLine, compactMode, t }: CreditLineCardProps) {
  const { language } = useUserLocation();

  // Calculated values from credit line data
  const isFactoring = creditLine?.productType === CreditLineProductType.FACTORING;
  const creditLimit = creditLine ? Number(creditLine.limit) : 0;
  const currentDebt = creditLine ? Number(creditLine.totalDebt) : 0;
  const collateral = Number(creditLine?.collateral ?? 0) || 0;
  const factoringAvailability = Number(creditLine?.availability ?? 0);
  const isNegativeAvailability = isFactoring && factoringAvailability < 0;
  const capReference = isFactoring ? getFactoringCap(creditLimit, collateral) : creditLimit;
  const availableCredit = isFactoring ? factoringAvailability : creditLimit - currentDebt;
  const utilizationPercentage = isFactoring
    ? getFactoringRemainingPercent(capReference, currentDebt)
    : capReference > 0
    ? (availableCredit / capReference) * 100
    : 0;
  const amountColor = isNegativeAvailability ? COLORS.feedback.danger : COLORS.brand.night;
  const valueFontSize = compactMode ? '12px' : '14px';
  const showLabels = !compactMode || isFactoring;

  function renderContent() {
    return (
      <Section isFullHeight sx={{ background: COLORS.brand.white, flex: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Typography sx={{ color: '#66758F', fontSize: compactMode ? '14px' : '18px', fontWeight: 500 }}>
              {t(
                isFactoring
                  ? 'revolving-credit-line:summary.availability'
                  : 'revolving-credit-line:summary.availableCredit'
              )}
            </Typography>
          </Grid>

          {/* Amount */}
          <Grid item xs={12}>
            <Typography sx={{ color: amountColor, fontSize: compactMode ? '18px' : '24px', fontWeight: 600 }}>
              {formatCreditLineCurrency(language, availableCredit, 'USD', 2)}
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ py: '1.5rem' }}>
            <ProgressBar
              progress={utilizationPercentage}
              tooltipTitle={t('revolving-credit-line:summary.creditUtilizedTooltip', {
                amount: formatNumericValue(currentDebt, 2),
              })}
            />
          </Grid>

          <Grid item xs={isFactoring ? 4 : 6}>
            {showLabels && (
              <Typography textAlign="start" sx={{ fontSize: valueFontSize }}>
                {t(
                  isFactoring
                    ? 'revolving-credit-line:summary.debt'
                    : 'revolving-credit-line:summary.creditUtilized'
                )}
              </Typography>
            )}
            <Typography
              textAlign="start"
              sx={{ color: COLORS.indigo[500], fontWeight: 600, fontSize: valueFontSize }}
            >
              {formatCreditLineCurrency(language, currentDebt, 'USD', 2)}
            </Typography>
          </Grid>
          {isFactoring && (
            <Grid item xs={4}>
              {showLabels && (
                <Typography textAlign="center" sx={{ fontSize: valueFontSize }}>
                  {t('revolving-credit-line:summary.collateral')}
                </Typography>
              )}
              <Typography
                textAlign="center"
                sx={{ color: COLORS.indigo[500], fontWeight: 600, fontSize: valueFontSize }}
              >
                {formatCreditLineCurrency(language, collateral, 'USD', 2)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={isFactoring ? 4 : 6}>
            {showLabels && (
              <Typography textAlign="end" sx={{ fontSize: valueFontSize }}>
                {t('revolving-credit-line:summary.creditLimit')}
              </Typography>
            )}
            <Typography
              textAlign="end"
              sx={{ color: COLORS.indigo[500], fontWeight: 600, fontSize: valueFontSize }}
            >
              {formatCreditLineCurrency(language, isFactoring ? creditLimit : capReference, 'USD', 2)}
            </Typography>
          </Grid>
          {isNegativeAvailability && !compactMode && (
            <Grid item xs={12} sx={{ pt: '1rem' }}>
              <Banner
                type={BannerType.Error}
                title={t('revolving-credit-line:summary.negativeAvailabilityTitle')}
                description={t('revolving-credit-line:summary.negativeAvailabilityMessage', {
                  amount: formatCreditLineCurrency(language, Math.abs(factoringAvailability), 'USD', 2),
                })}
              />
            </Grid>
          )}
        </Grid>
      </Section>
    );
  }

  if (compactMode) return renderContent();
  else
    return (
      <Grid item xs={12} md={6} lg={5}>
        {renderContent()}
      </Grid>
    );
}
